import { DT } from './constants';
import type { Content, Input, World } from './types';
import { applyCommands } from './systems/commands';
import { updateStructures, updateProjectiles, updateDrones, sweepDead } from './systems/combat';
import { updateCloaks, updateEnemies } from './systems/movement';
import { updateRotation } from './systems/rotation';
import { updateWave } from './systems/spawning';
import { updateEffects, updateStormHits } from './systems/ultimate';
import { updateDrops } from './systems/drops';

/**
 * One fixed 60Hz tick. Order matters:
 *   commands → rotation → spawning → movement → visibility → guns →
 *   projectiles → effects → drops → cleanup
 *
 * Rotation runs before movement so a spin the player commits this frame is
 * reflected in the coverage check the same frame — anything else makes the
 * ring feel a tick behind the thumb, which is exactly the thing the whole
 * design cannot afford.
 */
export function step(world: World, content: Content, input: Input, dt: number = DT): World {
  if (world.waveState === 'won' || world.waveState === 'lost') return world;

  applyCommands(world, content, input.commands);

  updateRotation(world, content, input, dt);
  updateWave(world, content, dt);
  updateEnemies(world, content, dt);
  updateCloaks(world, content);
  updateStructures(world, content, dt);
  updateProjectiles(world, content, dt);
  updateStormHits(world, content);
  updateDrones(world, content, dt);
  updateEffects(world, content, dt);
  updateDrops(world, dt);
  sweepDead(world, content);

  if (world.ultCd > 0) world.ultCd = Math.max(0, world.ultCd - dt);

  world.tick++;
  world.time += dt;
  return world;
}

/**
 * Accumulator loop for the render host. The sim only ever advances in whole
 * fixed ticks; `alpha` is handed to the renderer for interpolation.
 */
export class StepDriver {
  private accumulator = 0;

  constructor(
    private readonly world: World,
    private readonly content: Content,
  ) {}

  /** `speed` is the 1× / 2× / 0.35× toggle. Returns the interpolation alpha. */
  advance(elapsed: number, input: Input, speed = 1): number {
    // Clamp so a backgrounded tab doesn't come back and run 900 ticks at once.
    this.accumulator += Math.min(elapsed, 0.25) * speed;
    let ticks = 0;
    while (this.accumulator >= DT && ticks < 8) {
      // Commands apply once, on the first tick of the frame — replaying them
      // per tick would build the same structure three times.
      step(this.world, this.content, ticks === 0 ? input : consumed(input), DT);
      this.accumulator -= DT;
      ticks++;
    }
    return this.accumulator / DT;
  }
}

const CONSUMED: Input = { commands: [] };
function consumed(_input: Input): Input {
  return CONSUMED;
}
